// @flow
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { stylesheet } from 'typestyle';
import * as csx from 'csstips';
import classnames from 'classnames';
import numeral from 'numeral';
import _throttle from 'lodash/throttle';
import _isEqual from 'lodash/isEqual';

type TProps = {
  tableMinimumWidth: number,
  stickyContainerRef: any
};

const css = stylesheet({
  jumpbar: {
    ...csx.horizontal,
    ...csx.center,
    ...csx.betweenJustified,
    height: '50px',
    background: 'white',
    fontSize: '14px',
    color: '#848484'
  },
  jumpbarEnds: {
    cursor: 'pointer',
    padding: '0 20px',
    color: '#21528d',
    fontWeight: '700',
    $nest: {
      '&:hover': {
        textDecoration: 'underline'
      },
      '&:first-child': {
        padding: '0 10px 0 20px'
      },
      '&:last-child': {
        padding: '0 20px 0 10px'
      },
      '& a:hover': {
        color: '#21528d'
      }
    }
  },
  diamondCount: {
    '@media (max-width: 499px)': {
      display: 'none'
    }
  }
});

function ComparisonLink () {
  const count = useSelector((state) => state.diamondComparison.ids.length);

  if (count && count >= 2) {
    return [
      <i
        className="fa fa-clone"
        aria-hidden="true"
        style={{ marginRight: '4px' }}
        key="icon"
      />,
      <a href="/saved" key="link">
        Compare {count} Diamonds
      </a>
    ];
  } else return null;
}

function Jumpbar ({ tableMinimumWidth, stickyContainerRef }: TProps) {
  const jumpbarRef = useRef(null);
  const prevScrollLeftRef = useRef(null);
  const nextScrollLeftRef = useRef(null);
  const [styles, setStyles] = useState({});
  const showFilters = useSelector((state) => state.diamondSearch.showFilters);
  const filters = useSelector((state) => state.diamondSearchFilters);
  const diamondComparisonCount = useSelector(
    (state) => state.diamondComparison.ids.length
  );
  const diamondMatchesCount = useSelector((state) => state.diamonds.count);
  const isSearchingAndIsNewSearch = useSelector(
    (state) => state.diamondSearch.isSearchingAndIsNewSearch
  );
  const dispatch = useDispatch();
  const toggleFilterVisibility = () => {
    dispatch({ type: 'TOGGLE_FILTERS' });

    if (
      typeof document !== 'undefined' &&
      document.documentElement !== 'undefined' &&
      typeof window !== 'undefined'
    ) {
      const docEl = document.documentElement;
      const canScrollDown = docEl.scrollTop < docEl.scrollHeight;

      window.setTimeout(() => {
        // This forces the jumpbar to be rendered sticky immediately rather than
        // waiting until the user scrolls the page
        if (canScrollDown) {
          docEl.scrollTop += 1;
          docEl.scrollTop -= 1;
        } else {
          docEl.scrollTop -= 1;
          docEl.scrollTop += 1;
        }
      }, 500);
    }
  };

  const updateJumpbarPositionStyles = useCallback(
    () => {
      const nextScrollLeft = nextScrollLeftRef.current;
      const prevScrollLeft = prevScrollLeftRef.current;

      if (!stickyContainerRef || !jumpbarRef) return;
      if (prevScrollLeft === nextScrollLeft) return;

      setStyles({
        top: 0,
        left: Math.abs(parseInt(nextScrollLeft || 0)),
        right: nextScrollLeft ? -nextScrollLeft : 0,
        position: 'absolute'
      });
    },
    [stickyContainerRef]
  );

  const cacheThenUpdateJumpbarPositionStyles = useCallback(
    _throttle((e) => {
      const stickyContainer = stickyContainerRef.current;
      const jumpbar = jumpbarRef.current;

      if (!stickyContainer || !jumpbar) return;
      prevScrollLeftRef.current = nextScrollLeftRef.current;

      // Use window.winnerWidth as a starting default width
      const clientWidth = document.body
        ? document.body.clientWidth
        : window.innerWidth;

      let scrollLeft;
      let offsetWidth;

      if (e && e.type === 'scroll') {
        scrollLeft = e.target.scrollLeft;
        offsetWidth = e.target.offsetWidth;
      } else {
        scrollLeft = stickyContainer.scrollLeft;
        offsetWidth = stickyContainer.offsetWidth;
      }

      if (clientWidth < tableMinimumWidth) {
        nextScrollLeftRef.current = Math.min(
          scrollLeft,
          tableMinimumWidth - offsetWidth
        );
      } else {
        nextScrollLeftRef.current = 0;
      }

      updateJumpbarPositionStyles();
    }, 15),
    [
      jumpbarRef,
      stickyContainerRef,
      updateJumpbarPositionStyles,
      tableMinimumWidth
    ]
  );

  useLayoutEffect(
    () => {
      cacheThenUpdateJumpbarPositionStyles();
    },
    [cacheThenUpdateJumpbarPositionStyles, filters]
  );

  useEffect(
    () => {
      if (typeof window !== 'undefined') {
        window.addEventListener('resize', cacheThenUpdateJumpbarPositionStyles);
      }

      const stickyContainer = stickyContainerRef.current;

      if (stickyContainer) {
        stickyContainer.addEventListener(
          'scroll',
          cacheThenUpdateJumpbarPositionStyles
        );
        cacheThenUpdateJumpbarPositionStyles();
      }

      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener(
            'resize',
            cacheThenUpdateJumpbarPositionStyles
          );
        }

        if (stickyContainer) {
          stickyContainer.removeEventListener(
            'scroll',
            cacheThenUpdateJumpbarPositionStyles
          );
        }
      };
    },
    [cacheThenUpdateJumpbarPositionStyles, stickyContainerRef]
  );

  return (
    <div className={css.jumpbar} ref={jumpbarRef} style={styles}>
      <div className={css.jumpbarEnds}>
        <ComparisonLink />
      </div>
      <div className={diamondComparisonCount >= 2 ? css.diamondCount : ''}>
        {isSearchingAndIsNewSearch ? (
          'Searching...'
        ) : (
          `${numeral(diamondMatchesCount).format('0,0')} Diamonds Found`
        )}
      </div>
      <div className={css.jumpbarEnds} onClick={toggleFilterVisibility}>
        <i
          className={classnames('fa', {
            'fa-angle-up': showFilters,
            'fa-angle-down': !showFilters
          })}
          style={{ marginRight: '4px' }}
          aria-hidden="true"
        />
        {showFilters ? 'Hide' : 'Show'} Filters
      </div>
    </div>
  );
}

export default memo(Jumpbar, _isEqual);
